import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import classNames from 'classnames';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import type { ParsedUrlQuery } from 'querystring';
import { SUPPORTED_IGLIDE_IBH_LOCALES } from '@hub/libs/bearing-hub.helper';
import { IbhShape } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { ShapeCard } from '@hub/bearing-hub/BearingHubPage/shapes/ShapeCard/ShapeCard';

export interface ShapeNavigationProps {
  useTiles: boolean;
  selectedShape: ShapeDescriptor;
  availableShapes: ShapeDescriptor[];
  query: ParsedUrlQuery;
  locale: string;
}

export const ShapeNavigation = ({ useTiles, selectedShape, availableShapes, query, locale }: ShapeNavigationProps) => {
  const { t } = useTranslation(['bearing-hub/bearingHub', 'common']);
  const [searchParams, setSearchParams] = useState('');
  const [clientReady, setClientIsReady] = useState(false);
  const shapeOrder = [
    IbhShape.S,
    IbhShape.F,
    IbhShape.VS,
    IbhShape.VF,
    IbhShape.T,
    IbhShape.TE,
    IbhShape.PR,
    IbhShape.C,
    IbhShape.Y,
    IbhShape.YK,
    IbhShape.EC,
    IbhShape.D,
    IbhShape.K,
    IbhShape.FL,
    IbhShape.VFL,
    IbhShape.FSG,
  ];

  const hiddenShapes: IbhShape[] = [];
  /**
   * do not transfer currently selected dimensions to the newly selected shape
   * dimensions should come from dimensionsValueStore only
   */
  const removeDimensions = (_: string, hash: string, __: string, ___: string, filterParams: string): string => {
    if (filterParams) {
      return [hash, filterParams].join('');
    }
    return '';
  };

  useEffect(() => {
    setClientIsReady(true);
  }, []);

  useEffect(() => {
    if (clientReady) {
      setSearchParams(window.location.hash.replace(/(#)(dimensions=[^&]+)([&])?(.*)/, removeDimensions) || '');
    }
  }, [query]);

  const sortedAvailableShapes = availableShapes
    .sort((a, b) => shapeOrder.indexOf(a.shapeKey) - shapeOrder.indexOf(b.shapeKey))
    .filter(shape => !hiddenShapes.includes(shape.shapeKey));

  return useTiles ? (
    <div
      className="grid grid-cols-[repeat(auto-fit,minmax(125px,1fr))] lg:grid-cols-6 xl:grid-cols-8 gap-2 py-4 bg-gray-100"
      data-testid="navigation-list"
    >
      {sortedAvailableShapes.map(shape => (
        <div key={`${shape.shapeKey}`} className="flex justify-center">
          <ShapeCard
            shape={shape}
            selectedShape={selectedShape.shapeKey}
            searchParams={searchParams}
            clientReady={clientReady}
          />
        </div>
      ))}
    </div>
  ) : (
    <ul className="igus-text-styles mb-8" data-testid="navigation-list-mobile">
      {sortedAvailableShapes.map(shape => (
        <li className="mb-4" key={shape.shapeKey}>
          <Link
            prefetch={false}
            href={`/${SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? 'iglide-ibh' : 'iglidur-ibh'}/${
              shape.shapePath
            }${searchParams}`}
            data-testid={`navigation-link-${shape.shapeKey}`}
            className={classNames(selectedShape.shapeKey === shape.shapeKey ? 'active' : '')}
          >
            {clientReady && t(`SHAPES.${shape.shapeKey}.SHAPE_MENU`, { ns: 'bearing-hub/bearingHub' })}
          </Link>
        </li>
      ))}
    </ul>
  );
};
